import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  type LearnCardControls,
  useRegisterLearnCardControls,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { NewMorphemeLessonLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme._context";
import { useMemo } from "react";
import { useNewMorphemeLessonRevealPressMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme._lib.reveal";

/**
 * What the CURRENT lesson card lends the deck: its action snapshot (the grapheme and level, plus
 * `part` for a pronunciation-level lesson), and
 * the pronunciation stepping the action area's primary button drives while an introduced reading
 * is still unviewed. "Next Pronunciation" opens the first such reading's page and speaks it; the
 * learner can also open it from the binder tab. Once every introduced reading has been viewed, the
 * button reads "Next Card".
 */
export function LessonFlashcardControls() {
  const { mutate: reveal } = useNewMorphemeLessonRevealPressMutation();
  const [
    grapheme,
    hasUnviewedPronunciation,
    lesson,
    lessonPart,
    nextUnviewedPronunciationPageIndex,
    onlyPinyin,
    playForPinyin,
    setActiveIndex,
    studyPinyinList,
    viewNavigationRef,
  ] = useContextSelectors(
    NewMorphemeLessonLayoutContext,
    (c) => c.grapheme,
    (c) => c.hasUnviewedPronunciation,
    (c) => c.lesson,
    (c) => c.lessonPart,
    (c) => c.nextUnviewedPronunciationPageIndex,
    (c) => c.onlyPinyin,
    (c) => c.playForPinyin,
    (c) => c.setActiveIndex,
    (c) => c.studyPinyinList,
    (c) => c.viewNavigationRef,
  );
  const levelIndex = lesson.index;
  const isPronunciationLesson = lessonPart === "pronunciation";

  const controls = useMemo<LearnCardControls>(
    () => ({
      getActionSnapshot: async () =>
        isPronunciationLesson
          ? {
              grapheme,
              kind: "lesson",
              levelIndex,
              part: "pronunciation",
              ...(onlyPinyin === undefined ? {} : { pinyin: onlyPinyin }),
            }
          : { grapheme, kind: "lesson", levelIndex },
      hasUnviewedPronunciation,
      isPronunciationLesson,
      kind: "lesson",
      reveal,
      viewNextPronunciation: () => {
        if (nextUnviewedPronunciationPageIndex === undefined) {
          return;
        }
        const result = viewNavigationRef.current?.viewPronunciation(
          nextUnviewedPronunciationPageIndex,
        );
        if (result == null) {
          // The tab bar attaches `viewNavigationRef` in an effect; before it has, open the page
          // through the shared index directly so the control still progresses rather than
          // stranding the card.
          setActiveIndex(nextUnviewedPronunciationPageIndex);
          const pinyin = studyPinyinList[nextUnviewedPronunciationPageIndex - 1];
          if (pinyin !== undefined) {
            playForPinyin(pinyin);
          }
          return;
        }
        if (result.pinyin !== undefined) {
          playForPinyin(result.pinyin);
        }
      },
    }),
    [
      grapheme,
      hasUnviewedPronunciation,
      isPronunciationLesson,
      levelIndex,
      nextUnviewedPronunciationPageIndex,
      onlyPinyin,
      playForPinyin,
      reveal,
      setActiveIndex,
      studyPinyinList,
      viewNavigationRef,
    ],
  );
  useRegisterLearnCardControls(controls);

  return null;
}
